import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function WrapImperativePage(): Node {
  return html`
    <div>
      <h1>wrapImperative</h1>
      <p><code>wrapImperative</code> bridges imperative libraries (Chart.js, D3, Three.js) with onefold's reactive system. It manages lifecycle and re-renders when signals change.</p>

      <h2>Chart.js Example</h2>
      ${CodeBlock(`import { createSignal, html } from 'onefold';
import { wrapImperative } from 'onefold/interop';
import Chart from 'chart.js/auto';

function ReactiveChart(): Node {
  const data = createSignal([12, 19, 3, 5, 2, 3]);

  const chart = wrapImperative({
    create: (container) => {
      const canvas = document.createElement('canvas');
      container.appendChild(canvas);
      return new Chart(canvas, {
        type: 'bar',
        data: { labels: ['A', 'B', 'C', 'D', 'E', 'F'], datasets: [{ data: data() }] },
      });
    },
    update: (instance) => {
      instance.data.datasets[0].data = data();
      instance.update();
    },
    destroy: (instance) => {
      instance.destroy();
    },
    deps: [data],
  });

  return html\`
    <div>
      \${chart}
      <button onclick=\${() => data.set(d => d.map(() => Math.random() * 20))}>
        Randomize
      </button>
    </div>
  \`;
}`)}

      <h2>D3 Example</h2>
      ${CodeBlock(`import { createSignal } from 'onefold';
import { wrapImperative } from 'onefold/interop';
import * as d3 from 'd3';

function D3Visualization(): Node {
  const radius = createSignal(50);

  return wrapImperative({
    create: (container) => {
      const svg = d3.select(container).append('svg')
        .attr('width', 200).attr('height', 200);
      svg.append('circle')
        .attr('cx', 100).attr('cy', 100)
        .attr('r', radius())
        .attr('fill', 'steelblue');
      return svg;
    },
    update: (svg) => {
      svg.select('circle').attr('r', radius());
    },
    destroy: (svg) => {
      svg.remove();
    },
    deps: [radius],
  });
}`)}

      ${Callout('wrapImperative calls update() whenever any signal in deps changes. The imperative library stays in sync with reactive state.')}

      <h2>Options</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>create</code></td><td>(container: HTMLElement) => T</td><td>Initialize the imperative library. Return the instance.</td></tr>
        <tr><td><code>update</code></td><td>(instance: T) => void</td><td>Called when deps change. Update the instance.</td></tr>
        <tr><td><code>destroy</code></td><td>(instance: T) => void</td><td>Cleanup when the node is removed from DOM.</td></tr>
        <tr><td><code>deps</code></td><td>Signal[]</td><td>Signals to watch for changes.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>A canvas-based bar chart using <code>wrapImperative</code> — updates reactively when data changes:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { wrapImperative } from 'onefold/interop';

function App() {
  const data = createSignal([40, 70, 50, 90, 60, 80, 45]);

  function randomize() {
    data.set(data().map(() => Math.floor(Math.random() * 100) + 10));
  }

  // wrapImperative: mount an imperative canvas chart with auto-cleanup
  const chart = wrapImperative({
    tag: 'canvas',
    mount: (canvas) => {
      canvas.width = 320;
      canvas.height = 160;
      canvas.style.cssText = 'width:100%;height:160px;border:1px solid #e5e7eb;border-radius:8px';
      const ctx = canvas.getContext('2d');
      return ctx;
    },
    update: (ctx, canvas) => {
      const values = data();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barW = canvas.width / values.length - 8;
      const maxVal = Math.max(...values);
      values.forEach((val, i) => {
        const h = (val / maxVal) * (canvas.height - 20);
        const x = i * (barW + 8) + 4;
        const y = canvas.height - h - 10;
        ctx.fillStyle = 'hsl(' + (i * 45) + ', 70%, 55%)';
        ctx.beginPath();
        ctx.roundRect(x, y, barW, h, 4);
        ctx.fill();
        ctx.fillStyle = '#374151';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(val), x + barW / 2, canvas.height - 1);
      });
    },
    watch: () => data(),
  });

  return html\`
    <div>
      <h3>wrapImperative — Canvas Bar Chart</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        The canvas is managed imperatively. wrapImperative watches the signal and calls update() reactively.
      </p>
      \${chart}
      <button onclick=\${randomize} style="margin-top:12px">Randomize Data</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'wrapImperative — Reactive Canvas Chart')}
    </div>
  `;
}
